// Parcel lookup via index/ko.json and index/ko_{id}.json (SPEC §4, §4.1 step 1).

import { stripKoPrefix } from './assemble';
import { DataError, fetchJson } from './http';
import { normaliseParcelNo } from './parcelNo';
import { REGIONS, regionUrl } from './region';

export interface KoEntry { id: number; name: string; region: string }
export interface ParcelHit { ko: KoEntry; st: string; E: number; N: number; area: number; inRegion: boolean }

/** All covered cadastral municipalities, sorted by name. */
export async function listKo(): Promise<KoEntry[]> {
  const out: KoEntry[] = [];
  for (const r of REGIONS) {
    const idx = await fetchJson<Record<string, string>>(regionUrl(r.id, 'index/ko.json'));
    if (!idx) continue;
    for (const [id, name] of Object.entries(idx)) out.push({ id: +id, name: stripKoPrefix(id, name), region: r.id });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, 'sl'));
}

/** Resolve user k.o. input: an id ("1131"), a name ("Marija Dobje") or "1131 MARIJA DOBJE". */
export function matchKo(list: KoEntry[], q: string): KoEntry | undefined {
  const s = q.trim().toLocaleLowerCase('sl');
  if (!s) return undefined;
  const id = s.match(/^(\d{1,4})\b/);
  if (id) return list.find((k) => k.id === +id[1]);
  return list.find((k) => k.name.toLocaleLowerCase('sl') === s) ?? (() => {
    const hits = list.filter((k) => k.name.toLocaleLowerCase('sl').startsWith(s));
    return hits.length === 1 ? hits[0] : undefined;
  })();
}

export async function findParcel(koId: number, parcelInput: string): Promise<ParcelHit> {
  const list = await listKo();
  const ko = list.find((k) => k.id === koId);
  if (!ko) throw new DataError('ko_unknown', `k.o. ${koId} not covered`, { ko: koId, covered: list });
  const st = normaliseParcelNo(parcelInput);
  const idx = await fetchJson<Record<string, [number, number, number | null, number]>>(regionUrl(ko.region, `index/ko_${ko.id}.json`));
  const rec = idx?.[st];
  if (!rec) throw new DataError('parcel_unknown', `parcel ${st} not in k.o. ${ko.id}`, { ko, st });
  const [E, N, area, inRegion] = rec;
  if (!inRegion) throw new DataError('outside_region', `parcel ${st} outside the covered area`, { ko, st });
  return { ko, st, E, N, area: area ?? 0, inRegion: !!inRegion };
}
